/**
 * Documents Hook
 *
 * Manages document data and operations with HYBRID PAGINATION:
 * - First page (50 docs): Real-time onSnapshot with limit(50) for instant new-upload visibility
 * - Subsequent pages: Lightweight listProjectDocuments Cloud Function (excludes extractedText)
 *
 * Scaled for 500+ documents per project (Feb 2026).
 *
 * IMPORTANT: Upload error handling (Jan 2026)
 * - If file upload to Cloud Storage fails, the document is marked as 'failed'
 * - This prevents orphaned 'pending' documents that never get processed
 * - The upload itself lives in lib/documentUpload.ts (uploadProjectDocument)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db, functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { useTraining } from '../contexts/TrainingContext';
import { resolveDocumentUrl, triggerBrowserDownload, type DocumentDownloadResponse } from '../lib/documentDownload';
import { uploadProjectDocument } from '../lib/documentUpload';

// Inline (blob:) downloads are revoked after the browser has had time to start the save
const DOWNLOAD_BLOB_TTL_MS = 60_000;

// Pagination constants
const REALTIME_PAGE_SIZE = 50; // First page via onSnapshot (real-time)
const PAGINATED_PAGE_SIZE = 50; // Subsequent pages via Cloud Function

export interface Document {
  id: string;
  projectId: string;
  name: string;
  type: 'pdf' | 'docx' | 'xlsx' | 'txt' | 'pptx' | 'csv' | 'image' | 'archive' | 'email' | 'other';
  fileUrl: string;
  filePath: string;
  size: number;
  uploadedBy: string;
  extractionStatus: 'pending' | 'processing' | 'completed' | 'failed';
  extractionError?: string;
  extractionWarnings?: string[];
  textContent?: string;
  pageCount?: number;
  wordCount?: number;
  createdAt: Date;
  updatedAt: Date;
  // Priority/focus fields
  userPriority?: string;
  sourceAttribution?: string;
  focusedForChat?: boolean;
  focusedForIC?: boolean;
  focusedForDDQ?: boolean;
  // RAG status
  geminiStatus?: string;
  smartChunking?: Record<string, unknown>;
  extractedTextSizeBytes?: number;
  // Document category
  documentCategory?: string;
  metadata?: Record<string, unknown>;
  excludedFromAI?: boolean;
  // AI analysis
  aiAnalysis?: Record<string, unknown>;
}

export interface DocumentCounts {
  totalCount: number;
  completedCount: number;
  indexedCount: number;
  failedCount: number;
  pendingCount: number;
}

export const useDocuments = (projectId?: string) => {
  const [realtimeDocs, setRealtimeDocs] = useState<Document[]>([]);
  const [paginatedDocs, setPaginatedDocs] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [serverCounts, setServerCounts] = useState<DocumentCounts | null>(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const { trackFeature } = useTraining();
  const uploadCountRef = useRef(0);
  const lastDocIdRef = useRef<string | null>(null);
  const paginationStartedRef = useRef(false);

  // Merge realtime docs with paginated docs, deduplicating by ID
  const documents = useMemo(() => {
    const seen = new Set<string>();
    const merged: Document[] = [];

    // Real-time docs first (most recent)
    for (const doc of realtimeDocs) {
      if (!seen.has(doc.id)) {
        seen.add(doc.id);
        merged.push(doc);
      }
    }

    // Then paginated docs (older)
    for (const doc of paginatedDocs) {
      if (!seen.has(doc.id)) {
        seen.add(doc.id);
        merged.push(doc);
      }
    }

    return merged;
  }, [realtimeDocs, paginatedDocs]);

  // Real-time listener for first page (newest 50 docs)
  useEffect(() => {
    if (!projectId) {
      setRealtimeDocs([]);
      setPaginatedDocs([]);
      setLoading(false);
      setHasMore(false);
      lastDocIdRef.current = null;
      return;
    }

    const documentsQuery = query(
      collection(db, 'documents'),
      where('projectId', '==', projectId),
      orderBy('createdAt', 'desc'),
      limit(REALTIME_PAGE_SIZE)
    );

    const unsubscribe = onSnapshot(
      documentsQuery,
      (snapshot) => {
        const documentsData = snapshot.docs.map(doc => {
          const data = doc.data();
          // Strip heavy fields to prevent memory bloat
          const { extractedText, textContent, aiAnalysis, ...lightData } = data;
          return {
            id: doc.id,
            ...lightData,
            // Keep aiAnalysis reference for "AI Analyzed" badge but not the full content
            aiAnalysis: aiAnalysis ? { summary: aiAnalysis.summary } : undefined,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Document;
        });

        setRealtimeDocs(documentsData);
        setLoading(false);

        // If we got a full page, there are likely more documents. Once pagination has started
        // loadMore owns hasMore; a later realtime update must not flip it back to true.
        if (!paginationStartedRef.current) {
          setHasMore(snapshot.docs.length >= REALTIME_PAGE_SIZE);
        }

        // Track the last doc ID for pagination cursor (only before pagination starts)
        if (documentsData.length > 0 && !paginationStartedRef.current) {
          lastDocIdRef.current = documentsData[documentsData.length - 1].id;
        }
      },
      (err) => {
        console.error('Error fetching documents:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
      // Reset paginated docs when project changes
      setPaginatedDocs([]);
      lastDocIdRef.current = null;
      paginationStartedRef.current = false;
    };
  }, [projectId]);

  // Fetch server-side document counts (lightweight, no document reads)
  const fetchDocumentCounts = useCallback(async () => {
    if (!projectId) return;
    setCountsLoading(true);
    try {
      const getCounts = httpsCallable(functions, 'getProjectDocumentCounts');
      const result = await getCounts({ projectId }) as any;
      const data = result.data;
      setServerCounts({
        totalCount: data.totalCount ?? 0,
        completedCount: data.completedCount ?? 0,
        indexedCount: data.indexedCount ?? 0,
        failedCount: data.failedCount ?? 0,
        pendingCount: data.pendingCount ?? 0,
      });
      // Also update totalCount for backward compatibility
      if (data.totalCount != null) {
        setTotalCount(data.totalCount);
      }
    } catch (err: any) {
      console.error('Error fetching document counts:', err);
    } finally {
      setCountsLoading(false);
    }
  }, [projectId]);

  // Auto-fetch counts on mount and when projectId changes
  useEffect(() => {
    if (projectId) {
      fetchDocumentCounts();
    } else {
      setServerCounts(null);
    }
  }, [projectId, fetchDocumentCounts]);

  // Load more documents via Cloud Function (paginated, lightweight)
  const loadMore = useCallback(async () => {
    if (!projectId || !hasMore || loadingMore) return;

    setLoadingMore(true);
    setError(null);
    paginationStartedRef.current = true;
    try {
      const listDocs = httpsCallable(functions, 'listProjectDocuments');
      const result = await listDocs({
        projectId,
        pageSize: PAGINATED_PAGE_SIZE,
        startAfterDocId: lastDocIdRef.current,
      }) as any;

      const { documents: newDocs, hasMore: moreAvailable, totalCount: count } = result.data;

      if (count !== null && count !== undefined) {
        setTotalCount(count);
      }

      const parsed = (newDocs || []).map((doc: any) => ({
        ...doc,
        createdAt: doc.createdAt ? new Date(doc.createdAt) : new Date(),
        updatedAt: doc.updatedAt ? new Date(doc.updatedAt) : new Date(),
      })) as Document[];

      setPaginatedDocs(prev => [...prev, ...parsed]);
      setHasMore(!!moreAvailable);

      if (parsed.length > 0) {
        lastDocIdRef.current = parsed[parsed.length - 1].id;
      }
    } catch (err: any) {
      console.error('Error loading more documents:', err);
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [projectId, hasMore, loadingMore]);

  const uploadDocument = async (projectId: string, file: File): Promise<string> => {
    const documentId = await uploadProjectDocument(projectId, file);

    // Track document upload for training system
    uploadCountRef.current += 1;
    const milestone = uploadCountRef.current === 1 ? 'first-upload' : undefined;
    trackFeature('documents', milestone);

    // Refresh server-side counts after successful upload
    fetchDocumentCounts();

    return documentId;
  };

  const downloadDocument = useCallback(async (documentId: string, fileName?: string) => {
    try {
      const getDownloadUrl = httpsCallable<
        { documentId: string; download: boolean },
        DocumentDownloadResponse
      >(functions, 'getDocumentDownloadUrl', { timeout: 60_000 });
      const result = await getDownloadUrl({ documentId, download: true });

      const downloadUrl = resolveDocumentUrl(result.data, { revokeAfterMs: DOWNLOAD_BLOB_TTL_MS });
      if (!downloadUrl) {
        throw new Error('No download URL returned');
      }
      triggerBrowserDownload(downloadUrl, result.data.fileName || fileName);
    } catch (err: any) {
      console.error('Error downloading document:', err);
      throw new Error(err.message || 'Failed to download document');
    }
  }, []);

  const deleteDocument = useCallback(async (documentId: string) => {
    try {
      const deleteDoc = httpsCallable(functions, 'deleteDocument');
      await deleteDoc({ documentId });
    } catch (err: any) {
      console.error('Error deleting document:', err);
      throw new Error(err.message || 'Failed to delete document');
    }
  }, []);

  return {
    documents,
    loading,
    loadingMore,
    error,
    hasMore,
    totalCount,
    serverCounts,
    countsLoading,
    fetchDocumentCounts,
    loadMore,
    uploadDocument,
    downloadDocument,
    deleteDocument,
  };
};
